import React, { useState, useEffect, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { SiteStore } from '@stores/site/store';
import { Spin, Tooltip } from 'antd';
import { getEmojiDisplayValue, isEmojiImageUrl } from '@utils/emoji';

interface EmojiPickerProps {
    visible: boolean;
    onClose: () => void;
    onSelect: (emoji: string, emojiCode?: string, meta?: { category?: string }) => void;  // 第二个参数传递 code（用于纯文本场景）
    showTabs?: boolean;
    showEmojiTab?: boolean;
    closeOnSelect?: boolean;
    position?: {
        top?: number;
        left?: number;
        right?: number;
        bottom?: number;
    };
}

const EmojiPicker: React.FC<EmojiPickerProps & { site?: SiteStore }> = ({
    visible,
    onClose,
    onSelect,
    showTabs = true,
    showEmojiTab = false,
    closeOnSelect = true,
    position = {},
    site,
}) => {
    const [activeTab, setActiveTab] = useState<string>('');
    const pickerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!visible) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [visible, onClose]);

    // 如果站点未有表情数据，尝试拉取
    // useEffect(() => {
    //     if (visible && site && !site.hasEmoji && !site.isEmojiLoading) {
    //         (site as any).fetchEmoji?.();
    //     }
    // }, [visible, site]);

    const categories = (site?.emojiList as any[]) || [];
    const visibleCategories = categories.filter((cat: any) => showEmojiTab || cat.category !== 'emoji');
    const effectiveCategories = visibleCategories.length > 0 ? visibleCategories : categories;

    useEffect(() => {
        if (effectiveCategories.length === 0) return;
        const hasActiveCategory = effectiveCategories.some((cat: any) => cat.category === activeTab);
        if (!activeTab || !hasActiveCategory) {
            setActiveTab(effectiveCategories[0].category);
        }
    }, [effectiveCategories, activeTab]);

    // 初始化 activeTab：当有数据时设置为第一个分类
    if (!visible) return null;

    let activeCategory = effectiveCategories.find(cat => cat.category === activeTab);
    if (!activeCategory && effectiveCategories.length > 0) {
        activeCategory = effectiveCategories[0];
    }

    const visibleEmojis = showTabs
        ? (activeCategory?.emoji || []).map((emoji: any) => ({ ...emoji, __category: activeCategory?.category }))
        : effectiveCategories.flatMap((category: any) => (category?.emoji || []).map((emoji: any) => ({
            ...emoji,
            __category: category.category,
        })));

    const positionStyle: React.CSSProperties = {
        position: 'absolute',
        ...position
    };

    const handleEmojiClick = (emojiValue: string, emojiCode: string, category?: string) => {
        // 第一个参数传递 URL 或 code（优先 URL），第二个参数始终传递 code（用于纯文本场景）
        onSelect(emojiValue, emojiCode, { category });
        if (closeOnSelect) {
            onClose();
        }
    };

    return (
        <div className={styles.emojiPickerWrap} style={positionStyle} ref={pickerRef}>
            <Spin spinning={site?.isEmojiLoading}>
                {showTabs && (
                    <div className={styles.emojiHeader}>
                        {effectiveCategories.map((category: any) => (
                            <div
                                key={category.category}
                                className={`${styles.emojiTab} ${activeTab === category.category ? styles.emojiTabActive : ''}`}
                                onClick={() => setActiveTab(category.category)}
                            >
                                {category.category}
                            </div>
                        ))}
                    </div>
                )}
                {/* 表情列表 */}
                <div className={`${styles.emojiListWrap} ${!showTabs ? styles.emojiListWrapNoTabs : ''}`}>
                    {visibleEmojis.length ? (
                        visibleEmojis.map((emoji: any, index: number) => {
                            const isImageUrl = isEmojiImageUrl(emoji.url);
                            const isDefault = emoji.__category === '默认';
                            const itemClass = isImageUrl
                                ? (isDefault ? `${styles.emojiItem} ${styles.default28}` : `${styles.emojiItem} ${styles.vivoEmoji}`)
                                : `${styles.emojiItem} ${styles.defaultEmoji}`;
                            const imgClass = isDefault ? styles.emojiImgSmall : styles.emojiImgLarge;
                            const displayValue = getEmojiDisplayValue(emoji);
                            const emojiName = emoji.emoticoNname || emoji.code;

                            return (
                                <Tooltip
                                    key={emoji.id || emoji.code || index}
                                    title={emojiName}
                                    placement="bottom"
                                    arrow={false}
                                    mouseEnterDelay={0.15}
                                    overlayClassName={styles.emojiTooltip}
                                >
                                    <div
                                        className={itemClass}
                                        onClick={() => handleEmojiClick(displayValue, emoji.code, emoji.__category)}
                                    >
                                        {isImageUrl ? (
                                            <img
                                                src={emoji.url}
                                                alt={emojiName}
                                                className={imgClass}
                                            />
                                        ) : (
                                            <span className={styles.emojiText}>{displayValue}</span>
                                        )}
                                    </div>
                                </Tooltip>
                            );
                        })
                    ) : (
                        <div className={styles.empty}>暂无表情</div>
                    )}
                </div>
            </Spin>
        </div>
    );
};
export default inject('site')(observer(EmojiPicker));
